import { useQuery } from "@tanstack/react-query";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ShieldAlert } from "lucide-react";

/**
 * Checks whether the user's jurisdiction is blocked for prediction markets.
 * Pings the markets-api /markets endpoint — a 451 response means geo-blocked.
 */
function useGeoBlock() {
  return useQuery({
    queryKey: ["geo-block-check"],
    queryFn: async () => {
      const supabaseUrl =
        import.meta.env.VITE_SUPABASE_URL ||
        "https://eeiyqmrxxlwmetitkcrs.supabase.co";
      const res = await fetch(
        `${supabaseUrl}/functions/v1/markets-api/markets?limit=1`,
        { headers: { "Content-Type": "application/json" } }
      );
      if (res.status === 451) {
        const body = await res.json().catch(() => ({}));
        return { blocked: true, country: body.country || "unknown" };
      }
      return { blocked: false, country: "" };
    },
    staleTime: 5 * 60 * 1000, // cache for 5 min
    retry: false,
  });
}

/**
 * Wraps prediction market pages. Shows a jurisdiction notice if the user
 * is in a geo-blocked country, otherwise renders children normally.
 */
export function GeoGate({ children }: { children: React.ReactNode }) {
  const { data, isLoading } = useGeoBlock();

  if (isLoading) return null; // silent until check completes

  if (data?.blocked) {
    return (
      <div className="container mx-auto py-16 px-4 max-w-lg">
        <Alert variant="destructive" className="bg-destructive/10 border-destructive/30">
          <ShieldAlert className="h-5 w-5" />
          <AlertTitle>Unavailable in Your Region</AlertTitle>
          <AlertDescription>
            Prediction markets are not available in your jurisdiction
            ({data.country}). This restriction applies to market browsing,
            trading, and position management. DEX features (swap, pools,
            portfolio) remain fully accessible.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return <>{children}</>;
}
